import React, { useCallback } from 'react';
import { identity, prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';

import Slider from 'js/components/common/Slider';
import { ManagedPopup } from 'js/components/common/Popup';
import { WebContentInner } from 'js/components/system/WebContent';
import { DetailedTile } from 'js/app/components/main/Games/components/GameTile/components';

import { getOkButton } from 'js/components/common/popups/CommonPopup';
import { parseBonus } from 'js/components/popups/BonusWithGamesPopup/helpers';
import { updatePopupAction, useOnClose } from 'js/components/common/PopupManager';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { acceptBonusMessage } from 'js/components/popups/BonusWithGamesPopup/server';
import { getAcceptDeclineButtons } from 'js/components/common/popups/ConfirmationPopup';
import { updatePeriod as updateBonusHistoryPeriod } from 'js/components/main/BonusHistory/store/actions';
import { ACCEPT_BONUS, DECLINE_BONUS } from 'js/app/components/main/BonusCodePopup/constants';

import style from './BonusWithGamesPopup.scss';

const SLIDER_OPTIONS_MINI = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: true },
        bullets: { enabled: false },
        buttons: { enabled: false }
    }
};

export const BonusWithGamesPopup = ({
    id,
    title,
    message,
    dialogId,
    controls,
    isAccepted,
    rawBonusDetails,
    isConfirmation,
    syncActionsOnClose = identity,
    useDescription = identity,
    showGamesList
}) => {
    const closeManagedPopup = useOnClose();
    const games = useAppSelector(prop('games'));
    const lang = useUserSelector(prop('lang'));
    const bonusDetails = parseBonus({ data: rawBonusDetails, games, lang });
    const selectedGames = bonusDetails.bonusTemplate?.gamingContext?.selectedGames || [];
    const isModal = isConfirmation && !isAccepted;
    const dispatch = useDispatch();
    const portalDescription = useDescription();
    const isDesktop = useDeviceSelector(prop('isDesktop'));

    const handleClosePopup = useCallback(() => {
        closeManagedPopup();
        dispatch(updateBonusHistoryPeriod());
    }, [closeManagedPopup, dispatch]);

    const closeManagedAndSync = useCallback(() => {
        syncActionsOnClose();
        handleClosePopup();
    }, [handleClosePopup, syncActionsOnClose]);

    const onAcceptClick = useCallback(async () => {
        await acceptBonusMessage({ accept: true, dialogId });
        syncActionsOnClose();

        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }

        dispatcher.dispatch(ACCEPT_BONUS);
    }, [dialogId, id, syncActionsOnClose, dispatch, handleClosePopup, showGamesList]);

    const onDeclineClick = useCallback(async () => {
        await acceptBonusMessage({ accept: false, dialogId });
        closeManagedAndSync();
        dispatcher.dispatch(DECLINE_BONUS);
    }, [closeManagedAndSync, dialogId]);

    const onOkClick = useCallback(() => {
        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }

    }, [id, dispatch, handleClosePopup, showGamesList]);

    const acceptDeclineButtons = getAcceptDeclineButtons({ onAcceptClick, onDeclineClick });

    const buttons = isConfirmation && !isAccepted
        ? acceptDeclineButtons
        : getOkButton(isAccepted ? closeManagedAndSync : onOkClick);

    return (
        <ManagedPopup
            id={id}
            title={title}
            isModal={isModal}
            buttons={buttons}
            controls={controls}
            layerClassNames={style}
            onClose={handleClosePopup}
        >
            {isAccepted
                ? <>
                    <div className={style.description}>{portalDescription}</div>
                    <Slider
                        infinite
                        swipe={!isDesktop}
                        options={SLIDER_OPTIONS_MINI}
                        classNames={{
                            arrows: style.arrows,
                            slider: style.slider,
                            slide: style.slide
                        }}
                    >
                        {selectedGames.map((code) =>
                            <DetailedTile
                                key={code}
                                gameCodes={selectedGames}
                                gameCode={code}
                                disableHoverEffect
                                classNames={{
                                    gameTile: style.gameTile
                                }}
                            />
                        )}
                    </Slider>
                </>
                : <WebContentInner content={message} />
            }
        </ManagedPopup>
    );
};
